import React, { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import styles from "./transaction-result-v2.module.scss";
import { TransactionResultV2Props, TimelineStep } from "./types";
import { initializeTimelineFromHistorySteps, updateTimelineStepFromEvent } from "./constants";
import { useVendingStatusListener } from "../../../../hooks/useVendingStatusListener";
import { VendingHistoryStep } from "../../../../model/transaction";
import { formatTimestamp } from "../../../../utils";
import { ShareReceiptModal, TransactionReceiptHeader, VendingTransactionDetail } from "../../history/customer-history-detail/components";
import { shareReceiptAsImage } from "../../../../common/utils/receipt/shareReceiptAsImage";
import { shareReceiptAsPdf } from "../../../../common/utils/receipt/shareReceiptAsPdf";
import bgTransaction from "../../../../assets/background/bg-transaction.png";
import { useTenant } from "../../../../tenant/branding/useTenant";

const CheckmarkIcon: React.FC = () => (
	<img src={`${process.env.PUBLIC_URL ?? ""}/gifs/checkmark.gif`} alt="Success" width={130} height={130} className={styles.checkmarkGif} />
);

const SmallCheckIcon: React.FC<{ status: "completed" | "pending" | "failed" | "skipped" | "idle" }> = ({ status }) => {
	if (status === "completed") {
		return (
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
				<circle cx="12" cy="12" r="12" fill="#1FAF38" />
				<path d="M7 12.5 L10.5 16 L17 9" stroke="var(--app-base-white)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
		);
	}
	if (status === "failed") {
		return (
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
				<circle cx="12" cy="12" r="12" fill="var(--app-primary)" />
				<path d="M8 8 L16 16 M16 8 L8 16" stroke="var(--app-base-white)" strokeWidth="2" strokeLinecap="round" />
			</svg>
		);
	}
	if (status === "pending") {
		return (
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
				<circle cx="12" cy="12" r="10" stroke="var(--app-tooltip-bg)" strokeWidth="2" fill="#FFF6DD" />
				<circle cx="12" cy="12" r="3" fill="var(--app-tooltip-bg)" />
			</svg>
		);
	}
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
			<circle cx="12" cy="12" r="10" stroke="var(--app-primary-text-02)" strokeWidth="2" fill="var(--app-base-white)" />
		</svg>
	);
};

const ArrowDownIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
		<path d="M10 3v11M5 10l5 5 5-5" stroke="var(--app-primary-text)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const ShareIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
		<circle cx="15" cy="4.5" r="2.5" stroke="var(--app-primary-text)" strokeWidth="1.5" fill="none" />
		<circle cx="15" cy="15.5" r="2.5" stroke="var(--app-primary-text)" strokeWidth="1.5" fill="none" />
		<circle cx="5" cy="10" r="2.5" stroke="var(--app-primary-text)" strokeWidth="1.5" fill="none" />
		<path d="M12.5 6 7.5 8.5 M7.5 11.5 12.5 14" stroke="var(--app-primary-text)" strokeWidth="1.5" />
	</svg>
);

const CopyIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
		<rect x="6" y="6" width="11" height="11" rx="2" stroke="var(--app-primary-text)" strokeWidth="1.5" fill="none" />
		<path d="M14 6V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" stroke="var(--app-primary-text)" strokeWidth="1.5" fill="none" />
	</svg>
);

export const TransactionResultPageV2: React.FC<TransactionResultV2Props> = ({
	transaction,
	isLoading = false,
	onContinue,
	onSaveBeneficiary,
	isBeneficiarySaved = false,
	isSavingBeneficiary = false,
	onRevendToken,
	isRevending = false,
	onDownloadReceipt,
	isDownloadingReceipt = false,
	onShareReceipt,
	feePaid,
}) => {
	const [timelineSteps, setTimelineSteps] = useState<TimelineStep[]>(() => {
		const historySteps = transaction?.vendingDetails?.historySteps;
		return historySteps ? initializeTimelineFromHistorySteps(historySteps) : [];
	});
	const [showShareModal, setShowShareModal] = useState(false);
	const modalReceiptRef = useRef<HTMLDivElement>(null);
	const { logoWithText } = useTenant();

	const handleShareImage = () => {
		if (!modalReceiptRef.current) return;
		shareReceiptAsImage(modalReceiptRef.current, transaction?.reference ?? "receipt");
	};
	const handleSharePdf = () => {
		if (!modalReceiptRef.current) return;
		shareReceiptAsPdf(modalReceiptRef.current, transaction?.reference ?? "receipt");
	};

	useVendingStatusListener((vendingEvent: any) => {
		const historyStep: VendingHistoryStep = {
			typeOfEvent: typeof vendingEvent.typeOfEvent === "string" ? parseInt(vendingEvent.typeOfEvent, 10) : vendingEvent.typeOfEvent,
			subTypeOfEvent: typeof vendingEvent.subTypeOfEvent === "string" ? parseInt(vendingEvent.subTypeOfEvent, 10) : vendingEvent.subTypeOfEvent,
			vendingStepName: vendingEvent.vendingStepName || "",
			vendingStepStatusName: vendingEvent.vendingStepStatusName || "",
			timestamp: vendingEvent.timestamp,
			timeFormated: formatTimestamp(vendingEvent.timestamp) || "",
		};
		setTimelineSteps(prev => updateTimelineStepFromEvent(prev, historyStep));
	});

	const tokenGenerationFailed = useMemo(() => timelineSteps.some(s => s.id === "token_generation" && s.status === "failed"), [timelineSteps]);

	const vending = transaction?.vendingDetails;
	// const meterNumber = vending?.meterNumber ?? "—";
	const token = vending?.vendingToken?.token ?? "";
	const mapRefundToken = vending?.vendingToken?.mapRefundToken ?? "";
	// const meterType = transaction?.customerEnergyAccount?.consumerType === 2 ? "Postpaid" : "Prepaid";
	// const meterName = vending?.accountName ?? "";
	// const meterAddress = vending?.address ?? "";
	// const discoName = vending?.utility?.name ?? "";
	// const reference = transaction?.reference ?? "";
	// const amountValue = transaction?.amount?.amount ?? 0;
	// const amountCurrency = transaction?.amount?.currency ?? "NGN";
	// const purchaseDate = vending?.createdAt ?? transaction?.createdAt;
	// const sym = amountCurrency === "NGN" ? "₦" : amountCurrency === "USD" ? "$" : `${amountCurrency} `;

	const handleCopyToken = async (tokenValue: string) => {
		try {
			await navigator.clipboard.writeText(tokenValue);
			toast.success("Token copied to clipboard");
		} catch {
			toast.error("Couldn't copy token");
		}
	};

	if (isLoading) {
		return (
			<div className={styles.overlay}>
				<div className={styles.modal}>
					<div className={styles.loadingBlock}>
						<div className={styles.loadingSpinner} />
						<p>Loading transaction details…</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.overlay} onClick={onContinue}>
			<div className={styles.modal} onClick={e => e.stopPropagation()}>
				<div className={styles.modalScroll}>
					{/* Top bar: Download + Share */}
					<header className={styles.topBar}>
						<button type="button" className={styles.actionPill} onClick={onDownloadReceipt} disabled={isDownloadingReceipt}>
							<span>{isDownloadingReceipt ? "Preparing…" : "Download"}</span>
							<ArrowDownIcon />
						</button>
						<button type="button" className={styles.actionPill} onClick={() => setShowShareModal(true)}>
							<ShareIcon />
							<span>Share</span>
						</button>
					</header>

					{/* Hero */}
					<div className={styles.hero}>
						<CheckmarkIcon />
						<div className={styles.heroText}>
							<h1 className={styles.title}>Payment Successful</h1>
							<p className={styles.subtitle}>You can view this transaction in your transaction history</p>
						</div>
					</div>

					{/* Card */}
					<div className={styles.card}>
						{/* Recap rows (Amount / Type / Disco / Meter Type / Meter Number / Meter Name / Meter Address / Reference / Fee / Date)
				    hidden per design — uncomment to re-enable.
				<div className={styles.cardSection}>
					{amountValue > 0 && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Amount</span>
							<span className={styles.recapValue}>
								{sym}{amountValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
							</span>
						</div>
					)}
					<div className={styles.recapRow}>
						<span className={styles.recapLabel}>Type</span>
						<span className={styles.recapValue}>Electricity</span>
					</div>
					{discoName && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Disco</span>
							<span className={styles.recapValue}>{discoName}</span>
						</div>
					)}
					<div className={styles.recapRow}>
						<span className={styles.recapLabel}>Meter Type</span>
						<span className={styles.recapValue}>{meterType}</span>
					</div>
					<div className={styles.recapRow}>
						<span className={styles.recapLabel}>Meter Number</span>
						<span className={styles.recapValue}>{meterNumber}</span>
					</div>
					{meterName && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Meter Name</span>
							<span className={styles.recapValue}>{meterName}</span>
						</div>
					)}
					{meterAddress && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Meter Address</span>
							<span className={styles.recapValue}>{meterAddress}</span>
						</div>
					)}
					{reference && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Reference</span>
							<span className={styles.recapValue}>{reference}</span>
						</div>
					)}
					{feePaid !== undefined && feePaid !== null && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Fee</span>
							<span className={styles.recapValue}>
								{sym}{feePaid.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
							</span>
						</div>
					)}
					{purchaseDate && (
						<div className={styles.recapRow}>
							<span className={styles.recapLabel}>Date</span>
							<span className={styles.recapValue}>
								{formatTimestamp(purchaseDate.toString())}
							</span>
						</div>
					)}
				</div>
				*/}

						{timelineSteps.length > 0 && (
							<div className={styles.cardSection}>
								<ul className={styles.timeline}>
									{timelineSteps.map(step => (
										<li key={step.id} className={styles.timelineRow}>
											<SmallCheckIcon status={step.status} />
											<span className={styles.timelineLabel}>{step.label}</span>
											{step.timeFormated && <span className={styles.timelineTime}>{step.timeFormated}</span>}
										</li>
									))}
								</ul>
							</div>
						)}

						{token && (
							<div className={styles.cardSection}>
								<label className={styles.tokenLabel}>Your Token</label>
								<div className={styles.tokenField}>
									<input type="text" readOnly value={token} className={styles.tokenInput} />
									<button type="button" className={styles.tokenCopyBtn} onClick={() => handleCopyToken(token)}>
										<CopyIcon />
										<span>Copy</span>
									</button>
								</div>
							</div>
						)}

						{mapRefundToken && (
							<div className={styles.cardSection}>
								<label className={styles.tokenLabel}>MAP Token</label>
								<div className={styles.tokenField}>
									<input type="text" readOnly value={mapRefundToken} className={styles.tokenInput} />
									<button type="button" className={styles.tokenCopyBtn} onClick={() => handleCopyToken(mapRefundToken)}>
										<CopyIcon />
										<span>Copy</span>
									</button>
								</div>
							</div>
						)}

						{tokenGenerationFailed && onRevendToken && (
							<div className={styles.cardSection}>
								<button type="button" className={styles.retryBtn} onClick={onRevendToken} disabled={isRevending}>
									{isRevending ? "Retrying…" : "Retry token generation"}
								</button>
							</div>
						)}

						{onSaveBeneficiary && (
							<div className={`${styles.cardSection} ${styles.beneficiaryRow}`}>
								<span className={styles.beneficiaryLabel}>Save this beneficiary</span>
								<button
									type="button"
									className={`${styles.toggle} ${isBeneficiarySaved ? styles.toggleActive : ""}`}
									onClick={() => {
										void onSaveBeneficiary();
									}}
									disabled={isSavingBeneficiary}
									aria-pressed={isBeneficiarySaved}
								>
									<span className={styles.toggleKnob} />
								</button>
							</div>
						)}
					</div>
				</div>

				{/* Fixed footer */}
				<div className={styles.modalFooter}>
					<button type="button" className={styles.backToHomeBtn} onClick={onContinue}>
						Back to Home
					</button>
				</div>
			</div>

			{showShareModal && transaction && (
				<ShareReceiptModal
					ref={modalReceiptRef}
					onClose={() => setShowShareModal(false)}
					onShareImage={handleShareImage}
					onSharePdf={handleSharePdf}
					bgImage={bgTransaction}
					watermarkSrc={typeof logoWithText === "string" ? logoWithText : undefined}
					receiptStyle={{ "--receipt-circle-color": "var(--customer-bg)" } as React.CSSProperties}
				>
					<TransactionReceiptHeader
						status={transaction.status}
						amount={transaction.amount?.amount ?? 0}
						currency={transaction.amount?.currency}
						createdAt={transaction.createdAt}
						shareView={true}
					/>
					<VendingTransactionDetail
						transaction={transaction}
						showTokenView={false}
						onHideTokenView={() => {
							/* noop */
						}}
						onDownloadReceipt={() => {
							onDownloadReceipt?.();
						}}
						isDownloading={isDownloadingReceipt}
						shareView={true}
					/>
				</ShareReceiptModal>
			)}
		</div>
	);
};
